import { useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Paper,
  Snackbar,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';

import {
  useProjectDefaultTreatmentCosts,
  useUpdateProjectDefaultTreatmentCosts,
} from '../../api/project';
import { useUnitSystem } from '../../hooks/useUnitSystem';
import type { ProjectDefaultTreatmentCost } from '../../types/projectDefaultTreatmentCost';
import {
  toDisplayCostPerArea,
  toDisplayCostPerDistance,
  toMetricCostPerArea,
  toMetricCostPerDistance,
} from '../../utils/units';

type DefaultTreatmentCostsTabProps = {
  projectId: string;
};

type EditableRow = {
  treatment_type: string;
  cost_per_linear_km: string;
  cost_per_ha: string;
};

type SnackbarState = {
  open: boolean;
  message: string;
  severity: 'success' | 'error';
};

const initialSnackbar: SnackbarState = {
  open: false,
  message: '',
  severity: 'success',
};

const getEditableRows = (
  defaults: ProjectDefaultTreatmentCost[] | undefined,
  unitSystem: 'metric' | 'imperial',
): EditableRow[] => {
  if (!defaults) {
    return [];
  }

  return defaults.map((item) => ({
    treatment_type: item.treatment_type,
    cost_per_linear_km: formatNumberField(
      toDisplayCostPerDistance(item.cost_per_linear_km ?? 0, unitSystem)
    ),
    cost_per_ha: formatNumberField(
      toDisplayCostPerArea(item.cost_per_ha ?? 0, unitSystem)
    ),
  }));
};

const formatNumberField = (value: number | null | undefined): string => {
  if (value === null || value === undefined) {
    return '0';
  }

  return Number.isFinite(value) ? String(value) : '0';
};

const fieldKey = (treatmentType: string, field: keyof EditableRow) =>
  `${treatmentType}-${field}`;

export default function DefaultTreatmentCostsTab({ projectId }: DefaultTreatmentCostsTabProps) {
  const { data, isLoading, error, refetch } = useProjectDefaultTreatmentCosts(projectId);
  const mutation = useUpdateProjectDefaultTreatmentCosts(projectId);
  const unitSystem = useUnitSystem();

  const [rows, setRows] = useState<EditableRow[]>([]);
  const [originalRows, setOriginalRows] = useState<EditableRow[]>([]);
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});
  const [snackbar, setSnackbar] = useState<SnackbarState>(initialSnackbar);

  useEffect(() => {
    const editableRows = getEditableRows(data?.data, unitSystem);
    setRows(editableRows);
    setOriginalRows(editableRows);
    setFieldErrors({});
  }, [data?.data, unitSystem]);

  const hasChanges = useMemo(() => {
    if (rows.length !== originalRows.length) return true;
    
    return rows.some((row, index) => {
      const origRow = originalRows[index];
      return (
        row.treatment_type !== origRow.treatment_type ||
        row.cost_per_linear_km !== origRow.cost_per_linear_km ||
        row.cost_per_ha !== origRow.cost_per_ha
      );
    });
  }, [rows, originalRows]);

  const handleSnackbarClose = () => {
    setSnackbar((prev) => ({ ...prev, open: false }));
  };

  const handleFieldChange = (
    treatmentType: string,
    field: keyof Omit<EditableRow, 'treatment_type'>,
    value: string,
  ) => {
    setRows((prev) =>
      prev.map((row) =>
        row.treatment_type === treatmentType
          ? { ...row, [field]: value }
          : row,
      ),
    );

    const key = fieldKey(treatmentType, field);
    setFieldErrors((prev) => {
      const next = { ...prev };

      if (!isValidValue(value)) {
        next[key] = 'Value must be zero or positive';
      } else {
        delete next[key];
      }

      return next;
    });
  };

  const isValidValue = (value: string) => {
    if (value.trim() === '') {
      return false;
    }

    const parsed = Number(value);
    return Number.isFinite(parsed) && parsed >= 0;
  };

  const buildPayload = () => {
    return rows.map((row) => ({
      treatment_type: row.treatment_type,
      cost_per_linear_km: toMetricCostPerDistance(
        parseFloat(row.cost_per_linear_km || '0'),
        unitSystem
      ),
      cost_per_ha: toMetricCostPerArea(
        parseFloat(row.cost_per_ha || '0'),
        unitSystem
      ),
    }));
  };

  const handleSave = async () => {
    if (!rows.length) {
      return;
    }

    const invalidFields = rows
      .flatMap((row) => [
        { field: 'cost_per_linear_km', value: row.cost_per_linear_km },
        { field: 'cost_per_ha', value: row.cost_per_ha },
      ])
      .filter((entry) => !isValidValue(entry.value));

    if (invalidFields.length > 0 || Object.keys(fieldErrors).length > 0) {
      setSnackbar({
        open: true,
        severity: 'error',
        message: 'Please ensure all costs are zero or positive numbers.',
      });
      return;
    }

    try {
      await mutation.mutateAsync(buildPayload());
      await refetch();
      setSnackbar({
        open: true,
        severity: 'success',
        message: 'Default treatment costs updated successfully.',
      });
    } catch (mutationError) {
      console.error('Failed to update default treatment costs', mutationError);
      setSnackbar({
        open: true,
        severity: 'error',
        message: 'Failed to update default treatment costs.',
      });
    }
  };

  if (isLoading) {
    return (
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          py: 6,
          gap: 2,
          backgroundColor: '#1a1d2e',
          minHeight: '100%'
        }}
      >
        <CircularProgress sx={{ color: '#00d4ad' }} />
        <Typography variant="body1" sx={{ color: '#fff' }}>Loading default costs…</Typography>
      </Box>
    );
  }

  if (error) {
    return (
      <Alert 
        severity="error"
        sx={{
          backgroundColor: 'rgba(239, 83, 80, 0.1)',
          border: '1px solid rgba(239, 83, 80, 0.3)',
          color: '#ef5350'
        }}
      >
        Unable to load default treatment costs. Please try again later.
      </Alert>
    );
  }

  if (!rows.length) {
    return (
      <Alert 
        severity="info"
        sx={{
          backgroundColor: 'rgba(0, 212, 173, 0.1)',
          border: '1px solid rgba(0, 212, 173, 0.3)',
          color: '#00d4ad'
        }}
      >
        No treatment types available for this project.
      </Alert>
    );
  }

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, backgroundColor: '#1a1d2e', minHeight: '100%', p: 2 }}>
      <Typography variant="h6" sx={{ fontWeight: 500, color: '#fff' }}>
        Default Treatment Costs
      </Typography>


      <TableContainer component={Paper} sx={{ maxHeight: 'calc(var(--vh100) - 200px)', backgroundColor: '#252838', border: '1px solid rgba(255, 255, 255, 0.1)' }}>
        <Table stickyHeader>
          <TableHead>
            <TableRow>
              <TableCell sx={{ backgroundColor: '#2a2d3e', color: '#fff', fontWeight: 600, borderBottom: '1px solid rgba(255, 255, 255, 0.1)' }}>Treatment Type</TableCell>
              <TableCell sx={{ backgroundColor: '#2a2d3e', color: '#fff', fontWeight: 600, borderBottom: '1px solid rgba(255, 255, 255, 0.1)' }}>{unitSystem === 'imperial' ? 'Cost per mi' : 'Cost per km'}</TableCell>
              <TableCell sx={{ backgroundColor: '#2a2d3e', color: '#fff', fontWeight: 600, borderBottom: '1px solid rgba(255, 255, 255, 0.1)' }}>{unitSystem === 'imperial' ? 'Cost per acre' : 'Cost per ha'}</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {rows.map((row) => {
              const linearKey = fieldKey(row.treatment_type, 'cost_per_linear_km');
              const areaKey = fieldKey(row.treatment_type, 'cost_per_ha');

              return (
                <TableRow key={row.treatment_type} hover sx={{ '&:hover': { backgroundColor: 'rgba(255, 255, 255, 0.05)' } }}>
                  <TableCell sx={{ fontWeight: 500, color: '#fff', borderBottom: '1px solid rgba(255, 255, 255, 0.05)' }}>{row.treatment_type}</TableCell>
                  <TableCell sx={{ borderBottom: '1px solid rgba(255, 255, 255, 0.05)' }}>
                    <TextField
                      type="number"
                      value={row.cost_per_linear_km}
                      onChange={(event) =>
                        handleFieldChange(
                          row.treatment_type,
                          'cost_per_linear_km',
                          event.target.value,
                        )
                      }
                      inputProps={{ min: 0, step: 'any' }}
                      error={Boolean(fieldErrors[linearKey])}
                      helperText={fieldErrors[linearKey]}
                      fullWidth
                      size="small"
                      sx={{
                        '& .MuiOutlinedInput-root': {
                          '& fieldset': {
                            borderColor: 'rgba(255, 255, 255, 0.2)',
                          },
                          '&:hover fieldset': {
                            borderColor: 'rgba(255, 255, 255, 0.3)',
                          },
                          '&.Mui-focused fieldset': {
                            borderColor: '#00d4ad',
                          },
                        },
                        '& .MuiInputBase-input': {
                          color: '#fff',
                        },
                        '& .MuiFormHelperText-root': {
                          color: 'rgba(255, 255, 255, 0.7)',
                        },
                      }}
                    />
                  </TableCell>
                  <TableCell sx={{ borderBottom: '1px solid rgba(255, 255, 255, 0.05)' }}>
                    <TextField
                      type="number"
                      value={row.cost_per_ha}
                      onChange={(event) =>
                        handleFieldChange(
                          row.treatment_type,
                          'cost_per_ha',
                          event.target.value,
                        )
                      }
                      inputProps={{ min: 0, step: 'any' }}
                      error={Boolean(fieldErrors[areaKey])}
                      helperText={fieldErrors[areaKey]}
                      fullWidth
                      size="small"
                      sx={{
                        '& .MuiOutlinedInput-root': {
                          '& fieldset': {
                            borderColor: 'rgba(255, 255, 255, 0.2)',
                          },
                          '&:hover fieldset': {
                            borderColor: 'rgba(255, 255, 255, 0.3)',
                          },
                          '&.Mui-focused fieldset': {
                            borderColor: '#00d4ad',
                          },
                        },
                        '& .MuiInputBase-input': {
                          color: '#fff',
                        },
                        '& .MuiFormHelperText-root': {
                          color: 'rgba(255, 255, 255, 0.7)',
                        },
                      }}
                    />
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>

      <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
        <Button
          variant="contained"
          onClick={handleSave}
          disabled={!hasChanges || mutation.isPending}
          sx={{
            backgroundColor: '#00d4ad',
            color: '#000',
            '&:hover': {
              backgroundColor: '#00b894',
            },
            '&:disabled': {
              backgroundColor: 'rgba(255, 255, 255, 0.1)',
              color: 'rgba(255, 255, 255, 0.3)',
            },
          }}
        >
          {mutation.isPending ? 'Saving…' : 'Save Default Costs'}
        </Button>
      </Box>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={5000}
        onClose={handleSnackbarClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert 
          onClose={handleSnackbarClose} 
          severity={snackbar.severity} 
          variant="filled"
          sx={{ 
            width: '100%',
            backgroundColor: snackbar.severity === 'success' ? '#00d4ad' : '#ef5350',
            color: '#000'
          }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
}
